<template>
  <CardFlat
    id="median-price"
    hasInfoIcon
    :iconPosition="isIS24 ? 'right' : 'static'"
    :class="$style.card"
    @onInfoIconClick="isModalVisible = true"
  >
    <template #title>{{ title }}</template>
    <template #description>{{ description }}</template>

    <HgSkeleton
      v-if="isLoading"
      :height="isIS24 ? '70px' : '94px'"
      :class="$style.loadingWrapper"
    />

    <div v-else :class="$style.contentWrapper">
      <PriceContent v-if="medianPriceData[propertyType].price" :medianPriceData="medianPriceData" />
      <p v-else :class="$style.emptyPlaceholder">{{ t('common.noData') }}</p>
    </div>

    <p v-if="isHgMobileView" :class="$style.footerDescription">
      {{ t('averageMarketPrice.dateFrom', [medianPriceData[propertyType].date]) }}
    </p>

    <div
      ref="howBannerRef"
      :class="[$style.howBanner, isIS24 ? $style.backgroundColorIs24 : $style.backgroundColorHg]"
    >
      <img :src="howBannerImage" alt="" />
      <span :class="$style.howBannerText">{{ t('how.cta.text') }}</span>
      <HgButton @click="handlehowBannerClick">{{ t('how.cta.buttonText') }}</HgButton>
    </div>

    <HgModal
      id="median-price-modal"
      v-model="isModalVisible"
      mode="dialog"
      @close="isModalVisible = false"
    >
      <template #title> {{ t('averageMarketPrice.averageMarketPriceHeading') }}</template>
      <div
        v-for="{ title: modalTitle, description: modalDesc } in modalContent"
        :key="modalTitle"
        :class="$style.tooltipContentWrapper"
      >
        <p :class="$style.tooltipContentHeading">{{ modalTitle }}</p>
        <p :class="$style.tooltipContentDescription" v-html="modalDesc" />
      </div>
    </HgModal>
  </CardFlat>
</template>

<script lang="ts" setup>
import { HgButton, HgModal, HgSkeleton, HgSupportedLanguage } from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { computed, defineAsyncComponent, inject, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import howBannerIconHg from '@/assets/howBannerIconHg.webp';
import howBannerIconIs24 from '@/assets/howBannerIconIs24.webp';
import { CardFlat } from '@/components/common';
import adaptData, { type MedianPriceData } from '@/components/median-price/adaptData';
import { getAuthToken } from '@/helpers/auth/authClient';
import { isPlatformIs24 } from '@/helpers/platform';
import useGeoStore from '@/stores/useGeoStore';
import useMapStore from '@/stores/useMapStore';
import { trackHowBannerCtaClick, trackHowBannerIsVisible } from '@/tracking';
import { type MediaContext } from '@/views/composables/useMedia';

const baseUrl: string = import.meta.env.VUE_PLATFORM_BASE_URL;

const authToken = ref<string | undefined>();

const isIS24 = isPlatformIs24();

const PriceContent = defineAsyncComponent(() =>
  isIS24
    ? import('@/components/median-price/PriceContent.vue')
    : import('@/components/median-price/GraphicPriceContent.vue'),
);
const howBannerImage = computed<string>(() => (isIS24 ? howBannerIconIs24 : howBannerIconHg));

const { isSmallScreen } = inject<MediaContext>('mediaScreen') as MediaContext;

const isModalVisible = ref<boolean>(false);
const howBannerRef = ref<HTMLElement | null>(null);
let howBannerObserver: IntersectionObserver | null = null;
const { t, locale } = useI18n();
const { getLocationSentence } = storeToRefs(useGeoStore());
const mapStore = useMapStore();
const { data, propertyType, isLoading } = storeToRefs(mapStore);

const isHgMobileView = computed<boolean>(() => !isIS24 && !!isSmallScreen.value);

const medianPriceData = computed<MedianPriceData>(() =>
  adaptData(data.value.price, !isIS24, locale.value as HgSupportedLanguage),
);

const title = computed<string>(() =>
  isHgMobileView.value
    ? getLocationSentence.value(t, locale.value as HgSupportedLanguage, {
        includePreposition: false,
      })
    : t('averageMarketPrice.averageMarketPriceHeading'),
);

const description = computed<string>(() =>
  isHgMobileView.value
    ? t('averageMarketPrice.averageMarketPriceSubHeading.common')
    : isIS24
      ? (t('averageMarketPrice.averageMarketPriceSubHeading.' + propertyType.value, [
          getLocationSentence.value(t, locale.value as HgSupportedLanguage, {
            includePreposition: false,
          }),
        ]) as string)
      : (t('averageMarketPrice.dateFrom', [
          medianPriceData.value[propertyType.value].date,
        ]) as string),
);

const modalContent = computed<Array<{ title: string; description: string }>>(() => [
  {
    title: t('tooltips.marketVsOfferedTitle'),
    description: t('tooltips.marketVsOffered'),
  },
  {
    title: t('tooltips.dataUpdateAveragePriceTitle'),
    description: t('tooltips.dataUpdateAveragePrice'),
  },
  {
    title: t('tooltips.hedonicValuationTitle'),
    description: t('tooltips.hedonicValuation'),
  },
]);

const howValuationFormRoutes = {
  en: '/property-valuation/valuation-form',
  it: '/valutazione/modulo-di-valutazione',
  de: '/immobilienbewertung/bewertungsformular',
  fr: '/estimation/formulaire-evaluation',
};

const howLandingPageRoutes = {
  en: '/property-valuation/owners',
  it: '/valutazione/proprietari',
  de: '/immobilienbewertung/eigentuemer',
  fr: '/estimation/proprietaires',
};

const getLocalizedRoute = (route: string): string =>
  `/${locale.value as HgSupportedLanguage}${route}`;

const handlehowBannerClick = async () => {
  const resolvedAuthToken = authToken.value ?? (await getAuthToken());
  authToken.value = resolvedAuthToken;
  const isUserLoggedIn = !!resolvedAuthToken;
  const localizedValuationFormRoute = getLocalizedRoute(
    howValuationFormRoutes[locale.value as HgSupportedLanguage],
  );
  const localizedLandingPageRoute = getLocalizedRoute(
    howLandingPageRoutes[locale.value as HgSupportedLanguage],
  );

  if (isUserLoggedIn) {
    trackHowBannerCtaClick('go_to_valuation_form');
    window.location.href = `${baseUrl}${localizedValuationFormRoute}?utm_source=market_atlas`;
  } else if (isIS24) {
    trackHowBannerCtaClick('go_to_valuation_form');
    window.location.href = `${baseUrl}${localizedValuationFormRoute}?utm_source=market_atlas&login_required=true`;
  } else {
    trackHowBannerCtaClick('go_to_my_real_estate');
    window.location.href = `${baseUrl}${localizedLandingPageRoute}?utm_source=market_atlas`;
  }
};

onMounted(async () => {
  if (!howBannerRef.value) {
    return;
  }

  howBannerObserver = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        trackHowBannerIsVisible();
        howBannerObserver?.disconnect();
      }
    },
    { threshold: 0.2 },
  );

  howBannerObserver.observe(howBannerRef.value);

  authToken.value = await getAuthToken();
});

onBeforeUnmount(() => {
  howBannerObserver?.disconnect();
  howBannerObserver = null;
});
</script>

<style lang="scss" module>
@use '@/styles/variables' as *;

.contentWrapper {
  display: flex;
  flex-direction: column;
  padding-top: $space-100;
}

.loadingWrapper {
  margin: $space-100 $space-100 $space-100 0;
}

.emptyPlaceholder {
  align-self: center;
  margin-bottom: $space-200;
  color: $color-grey-90;

  @include body-3;
}

.footerDescription {
  margin-top: $space-100;
  margin-bottom: 0;
  text-align: right;

  @include caption;
}

.tooltipContentWrapper {
  margin-bottom: $space-250;
}

.tooltipContentDescription {
  margin-top: 0;
  margin-bottom: 0;

  @include body-2;
}

.tooltipContentHeading {
  margin-top: 0;
  margin-bottom: 0;

  @include heading-5;
}

.howBanner {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: $space-150;
  margin-top: $space-150;

  img {
    width: 96px;
    height: 96px;
  }

  .howBannerText {
    @include heading-4;
    padding: $space-75 0;
  }
}

.backgroundColorIs24 {
  background-color: $color-teal-50;
}

.backgroundColorHg {
  background-color: $color-magenta-0;
}
</style>
